import { useState } from "react";
import { Check, Copy, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, fieldErrorsFrom, messageFrom, warnQuietly } from "@/lib/api";
import { validateEmail } from "@/lib/validators";

/**
 * "Finish this later" panel.
 *
 * Honest by design: we do NOT send an email. We generate a resume link you can
 * copy and use on any device, and (optionally) save your address against the
 * session so the T-TITE team can follow up. This is completely separate from
 * the mandatory contact gate.
 */
export const ResumePanel = ({ sessionId, onEmailSaved }) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    const trimmed = email.trim();
    if (trimmed) {
      const invalid = validateEmail(trimmed);
      if (invalid) {
        setError(invalid);
        return;
      }
    }
    setError(null);
    setBusy(true);
    try {
      const data = await api.resumeLink(sessionId, { email: trimmed || null });
      setResult(data);
      if (data.email_saved && onEmailSaved) onEmailSaved(trimmed);
    } catch (err) {
      const fieldErrors = fieldErrorsFrom(err);
      if (fieldErrors?.email) setError(fieldErrors.email);
      else toast.error(messageFrom(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!result?.resume_url) return;
    try {
      await navigator.clipboard.writeText(result.resume_url);
      setCopied(true);
      toast.success("Resume link copied to your clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch (error) {
      warnQuietly("clipboard copy was refused", error);
      toast.error("We couldn't copy it automatically — please select the link and copy it.");
    }
  };

  return (
    <div data-testid="resume-link-panel" className="tt-card p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[color:var(--tt-teal-mist)] text-primary">
          <Mail className="h-4.5 w-4.5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-foreground">Need to finish this later?</h3>
          <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
            Your answers save automatically on this browser for 30 days. To carry on from a
            different device, generate a link below and keep it somewhere safe.
          </p>
        </div>
      </div>

      {!result ? (
        <div className="mt-4 space-y-3">
          <div>
            <Label
              htmlFor="resume-email"
              className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground"
            >
              Your email <span className="font-normal normal-case tracking-normal">(optional)</span>
            </Label>
            <Input
              id="resume-email"
              data-testid="resume-email-input"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) setError(null);
              }}
              placeholder="you@yourbusiness.co.za"
              aria-invalid={error ? "true" : "false"}
              className={`mt-1.5 h-11 ${error ? "border-destructive" : ""}`}
            />
            {error ? (
              <p role="alert" className="mt-1.5 text-[13px] font-medium text-destructive">
                {error}
              </p>
            ) : (
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                Adding it lets our team pick this up with you if you don't come back. We won't
                send you an automated email.
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={generate}
            disabled={busy}
            data-testid="resume-generate-button"
            className="h-11 w-full"
          >
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            Create my resume link
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-[color:var(--tt-teal-line)] bg-[color:var(--tt-teal-mist)] p-3">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[color:var(--tt-teal-deep)]">
              Your resume link · valid {result.expires_in_days} days
            </p>
            <input
              readOnly
              data-testid="resume-link-url"
              value={result.resume_url}
              onFocus={(event) => event.target.select()}
              className="mt-2 w-full rounded-md border border-[color:var(--tt-teal-line)] bg-white px-3 py-2 text-xs text-foreground"
              aria-label="Resume link"
            />
            <Button
              type="button"
              onClick={copy}
              data-testid="resume-link-copy-button"
              className="mt-2.5 h-10 w-full"
            >
              {copied ? (
                <Check className="mr-2 h-4 w-4" aria-hidden="true" />
              ) : (
                <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {result.email_saved
              ? "We've saved your email against this quote so our team can follow up. No automated email has been sent — the link above is yours to keep."
              : "No email has been sent — the link above is yours to keep."}
          </p>
        </div>
      )}
    </div>
  );
};
